import React, { useEffect, useRef, useState, useMemo } from 'react';
import { IMarkProps, TColorType } from './type.d';
import { FONT_SIZE, DEFAULT_CAPSULE_COLORS_MAP } from './constants';
import { getTitle } from './util';
import { useIntl } from 'umi';

export default function Mark(props: IMarkProps) {
  const { data, position } = props;
  const {
    markWidth,
    markHeight,
    isBold = false,
    colorType = 'skyBlue',
    titlePosition,
    zIndex,
    isHide,
    top: customTop,
    left: customLeft,
    preventMarkOverflow = false, // it will center the text for mark if its false (use only when title is too long)
  } = data;
  const titleEl = useRef(null);
  const [titleWidth, setTitleWidth] = useState<number>(0);
  const { formatMessage } = useIntl();

  const getGradientColor = (colorType: TColorType): string=> {
    const target = DEFAULT_CAPSULE_COLORS_MAP[colorType]?.capsule || '';
    return target?.split(',')?.[0];
  };

  const titleAbsoluteStyle = useMemo(() => {
    // 可自定义传入偏移量
    let top = 'unset';
    let left = 'unset';
    const toTop = markHeight ? `${(markHeight - FONT_SIZE)/2}px` : 'unset';
    switch (titlePosition) {
      case 'top':
        top = '-20px';
        // Adjust left position based on mark position to prevent overflow
        if (titleWidth) {
          if (preventMarkOverflow) {
            const halfWidth = titleWidth / 2;
            // If mark is near the beginning (<= 6%), align text to start (left: 0)
            if (position !== undefined && position <= 6) {
              left = '0';
            }
            // If mark is near the end (>= 95%), align text to end
            else if (position !== undefined && position >= 95) {
              left = `-${titleWidth}px`;
            }
            // Otherwise, center the text
            else {
              left = `-${halfWidth}px`;
            }
          } else {
            left = `-${titleWidth / 2}px`;
          }
        } else {
          left = '0';
        }
        break;
      case 'bottom':
        top = markHeight ? `${markHeight + 8}px` : 'unset';
        left = titleWidth ? `-${titleWidth / 2}px` : '0';
        break;
      case 'right':
        top = toTop;
        left = markWidth ? `${markWidth + 10}px` : 'unset';
        break;
      case 'left':
        top = toTop;
        left = markWidth ? `-${markWidth + 10}px` : 'unset';
        break;
      default:
        break;
    }
    top = customTop || top;
    left = customLeft || left;
    return {
      top,
      left,
    };
  }, [markWidth, markHeight, customTop, customLeft, titlePosition, titleWidth, position, preventMarkOverflow]);

  const title = useMemo(
    () =>
      getTitle(
        data,
        data?.localeTitleKey && data.localeTitleKey.startsWith('dbs_')
          ? formatMessage({ id: data.localeTitleKey })
          : undefined,
      ),
    [data, formatMessage],
  );

  useEffect(() => {
    if (titleEl.current) {
      const width = (titleEl.current as HTMLElement)?.offsetWidth; // 获取元素的宽度
      if (width) {
        setTitleWidth(width);
      }
    }
  }, []);

  return (
    <div
      className='capsule-bar-mark'
      style={{
        width: markWidth ? `${markWidth}px` : '2px',
        height: markHeight ? `${markHeight}px` : '100%',
        backgroundColor: getGradientColor(colorType) || '#000',
        left: position ? `${position}%` : 'unset',
        zIndex: zIndex || 105,
        display: isHide ? 'none' : 'block',
      }}>
      <div className='capsule-bar-mark-wrap'>
        <div
          ref={titleEl}
          className='capsule-bar-mark-title'
          style={{
            ...titleAbsoluteStyle,
            color: DEFAULT_CAPSULE_COLORS_MAP[colorType]?.text || '#fff',
            fontWeight: isBold ? 'bold' : 'normal',
          }}>
          {title}
        </div>
      </div>
    </div>
  );
};